/**
 * Platform — a MOVING solid platform (Brief 011 kinematic-solid family).
 *
 * Reuses the Brief 009 solid spine (`solidBody()` → player stands on / is blocked by
 * it like a tile) and adds deterministic MOTION: a ping-pong path along one axis,
 * `props.range` tiles out and back at `props.speed` px/s. The player standing on its
 * top is CARRIED with it — World reads {@link kinematicDelta} after this entity moves
 * and adds it to the rider before re-seating (no sink/jitter).
 *
 * Determinism (HARD): motion is a fixed-step parametric — an `offset` accumulator
 * advanced by `speed * dt` and REFLECTED at the ends. No `sin/cos`, no wall-clock, no
 * RNG, so the same seed + inputs replay identical platform positions (the replay/ghost
 * contract). Linear ping-pong is transcendental-free by construction — exactly the path
 * the brief says to prefer (a circular track would need the banned trig).
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_RANGE_TILES = 3;
const DEFAULT_SPEED = 80;    // px/s
const DEFAULT_W_TILES = 2;
const DEFAULT_H_TILES = 1;
// Brief 014 P1 falling/donut-platform mode.
const FALL_GRAVITY = 2200;   // px/s² once it drops
const FALL_DISTANCE = 8 * 64; // px it falls before disappearing
const DEFAULT_FALL_DELAY = 20;   // steps stood-on before it drops
const DEFAULT_FALL_RESPAWN = 120; // steps before it returns (0 = never)

function numProp(props: Record<string, unknown> | undefined, key: string, fallback: number): number {
  const v = props?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export class Platform implements Entity {
  readonly type = EntityType.PLATFORM;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private readonly axis: 'x' | 'y';
  private readonly span: number;   // px the platform travels out before reflecting
  private readonly sign: 1 | -1;   // travel direction along the axis (range sign)
  private readonly speed: number;  // px/s
  private readonly originX: number;
  private readonly originY: number;
  /** Distance (px) along the path from origin, in [0, span]. */
  private offset = 0;
  private dir: 1 | -1 = 1;
  /** Movement applied THIS step (for rider carry). */
  private dx = 0;
  private dy = 0;

  // ── Brief 014 P1 falling/donut mode (all inert unless `props.falling`) ──────────
  private readonly falling: boolean;
  private readonly fallDelay: number;   // steps stood-on before dropping
  private readonly fallRespawn: number; // steps gone before returning (0 = never)
  private fState: 'idle' | 'falling' | 'gone' = 'idle';
  private armed = false;
  private fallVel = 0;
  private fTimer = 0;

  /**
   * @param cx placed-cell center-x, @param feetY placed-cell bottom (the bar's bottom
   *   edge sits here; the bar is centered on the placed column).
   * @param props `axis` ('x'|'y', def 'x'), `range` (tiles, def 3 — its SIGN sets the
   *   travel direction: +x = right, -x = left, +y = DOWN, -y = UP, so a rising lift is
   *   `axis:'y', range:-N`), `speed` (px/s, def 80), `w`/`h` (tiles, def 2×1).
   */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const w = Math.max(1, numProp(props, 'w', DEFAULT_W_TILES)) * ts;
    const h = Math.max(1, numProp(props, 'h', DEFAULT_H_TILES)) * ts;
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    this.originX = this.body.x;
    this.originY = this.body.y;
    this.axis = props?.axis === 'y' ? 'y' : 'x';
    const rangeTiles = numProp(props, 'range', DEFAULT_RANGE_TILES);
    this.span = Math.abs(rangeTiles) * ts;
    this.sign = rangeTiles < 0 ? -1 : 1;
    this.speed = Math.max(0, numProp(props, 'speed', DEFAULT_SPEED));
    // Brief 014 P1 falling/donut mode. A falling platform does NOT ping-pong — it rests at
    // origin until stood on, then drops and (optionally) respawns.
    this.falling = props?.falling === true;
    this.fallDelay = Math.max(0, Math.floor(numProp(props, 'fallDelaySteps', DEFAULT_FALL_DELAY)));
    this.fallRespawn = Math.max(0, Math.floor(numProp(props, 'respawnSteps', DEFAULT_FALL_RESPAWN)));
  }

  /** Solid until a falling platform has dropped out of the world. */
  solidBody(): AABB | null { return this.falling && this.fState === 'gone' ? null : this.body; }

  update(dt: number, _ctx: EntityContext): void {
    const oldX = this.body.x;
    const oldY = this.body.y;
    if (this.falling) {
      this.tickFalling(dt);
    } else if (this.span > 0 && this.speed > 0) {
      this.offset += this.dir * this.speed * dt;
      if (this.offset > this.span) { this.offset = 2 * this.span - this.offset; this.dir = -1; }
      else if (this.offset < 0) { this.offset = -this.offset; this.dir = 1; }
      // Safety clamp for extreme params (step > 2·span) so the body never escapes the path.
      this.offset = Math.max(0, Math.min(this.span, this.offset));
      const travel = this.sign * this.offset; // signed by the range direction
      if (this.axis === 'x') this.body.x = this.originX + travel;
      else this.body.y = this.originY + travel;
    }
    this.dx = this.body.x - oldX;
    this.dy = this.body.y - oldY;
  }

  /** Falling/donut state machine (integer-step timers → replay-safe). */
  private tickFalling(dt: number): void {
    if (this.fState === 'idle') {
      if (this.armed) { this.fTimer--; if (this.fTimer <= 0) { this.fState = 'falling'; this.fallVel = 0; } }
    } else if (this.fState === 'falling') {
      this.fallVel += FALL_GRAVITY * dt;
      this.body.y += this.fallVel * dt;
      if (this.body.y > this.originY + FALL_DISTANCE) { this.fState = 'gone'; this.fTimer = this.fallRespawn; }
    } else if (this.fallRespawn > 0) { // gone → respawn
      this.fTimer--;
      if (this.fTimer <= 0) { this.body.x = this.originX; this.body.y = this.originY; this.fState = 'idle'; this.armed = false; this.fallVel = 0; }
    }
  }

  /** Falling mode: standing on it arms the drop (once). No-op for a normal platform. */
  onStand(_ctx: EntityContext, _landingVy: number): void {
    if (this.falling && this.fState === 'idle' && !this.armed) { this.armed = true; this.fTimer = this.fallDelay; }
  }

  /** Per-step movement — World carries the rider by this (Brief 011). */
  kinematicDelta(): { dx: number; dy: number } { return { dx: this.dx, dy: this.dy }; }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — interaction is via standing / carry */ }

  spriteKey(): string { return this.falling ? 'platform_falling' : 'platform'; }
  visible(): boolean { return !(this.falling && this.fState === 'gone'); }
  renderSize(): { w: number; h: number } { return { w: this.body.w, h: this.body.h }; }
}
